const DEFAULT_ALERTS = [
  { key: "rfqs_review", count: 0, label: "RFQs to review", tone: "red", href: "/dashboard/logistics/rfqs" },
  { key: "pending_approvals", count: 0, label: "Pending approvals", tone: "amber", href: "/dashboard/logistics/rfqs" },
  { key: "quotes_compare", count: 0, label: "Quotes to compare", tone: "blue", href: "/dashboard/logistics/rfqs" },
  { key: "delayed", count: 0, label: "Delayed items", tone: "red", href: "/dashboard/logistics/rfqs" },
  { key: "invoices", count: 0, label: "Invoices / reports", tone: "amber", href: "/dashboard/logistics/rfqs" },
  { key: "exceptions", count: 0, label: "Exceptions", tone: "blue", href: "/dashboard/notifications" },
];

const PIPELINE_LABELS = [
  { key: "rfq_created", label: "RFQ Created", tone: "orange" },
  { key: "quote_received", label: "Quote Received", tone: "orange-light" },
  { key: "approval", label: "Approval", tone: "orange-pale" },
  { key: "po", label: "PO", tone: "plain" },
  { key: "invoice", label: "Invoice", tone: "plain" },
  { key: "payment", label: "Payment", tone: "plain" },
  { key: "delivery", label: "Delivery", tone: "plain" },
  { key: "grn", label: "GRN", tone: "plain" },
];

function unwrapApiPayload(res) {
  if (!res || typeof res !== "object") return null;
  if (res.success === true && res.data != null) return res.data;
  return res;
}

function mapLegacyMarketplaceDashboard(payload) {
  const k = payload.kpis || {};
  const totalRfqs = Number(k.total_rfqs?.value) || 0;
  const newRfqs = Number(k.new_rfqs?.value) || 0;
  const quoted = Number(k.quotations_submitted?.value) || 0;
  const shortlisted = Number(k.shortlisted?.value) || 0;
  const contracts = Number(k.contracts_won?.value) || 0;
  const revenue = Number(k.revenue_this_month?.value) || 0;
  const perf = payload.performance || {};
  const pendingTask = (payload.tasks || []).find((t) => t.key === "pending_approval");
  const delayedTask = (payload.tasks || []).find((t) => t.key === "closing_soon");
  const invoiceTask = (payload.tasks || []).find((t) => t.key === "invoices");

  const alertCounts = {
    rfqs_review: newRfqs,
    pending_approvals: pendingTask?.count ?? 0,
    quotes_compare: shortlisted,
    delayed: delayedTask?.count ?? 0,
    invoices: invoiceTask?.count ?? 0,
    exceptions: newRfqs,
  };

  const statusAlerts = DEFAULT_ALERTS.map((a) => ({
    ...a,
    count: alertCounts[a.key] ?? 0,
  }));

  const keyMetrics = [
    { key: "active_rfqs", label: "Active RFQs", value: totalRfqs, sublabel: `${newRfqs} new` },
    { key: "quotations", label: "Quotations", value: quoted, sublabel: `${shortlisted} pending` },
    { key: "active_orders", label: "Active Orders", value: contracts, sublabel: `${contracts} live` },
    { key: "revenue", label: "Spend / Revenue", value: revenue, format: "currency", sublabel: "This month" },
    { key: "pending", label: "Pending", value: newRfqs, sublabel: "Needs action" },
    { key: "score", label: "Score", value: perf.win_rate ?? 96, format: "percent", sublabel: "Stable" },
  ];

  const pipelineCounts = {
    rfq_created: totalRfqs,
    quote_received: quoted,
    approval: pendingTask?.count ?? 0,
    po: contracts,
    invoice: invoiceTask?.count ?? 0,
    payment: 0,
    delivery: contracts,
    grn: 0,
  };

  const overviewPipeline = PIPELINE_LABELS.map((step) => ({
    ...step,
    count: pipelineCounts[step.key] ?? 0,
  }));

  const workQueue = (payload.latest_rfqs?.items || []).slice(0, 6).map((r) => ({
    id: `lp-rfq-${r.id}`,
    doc_no: r.rfq_no || `LRFQ-${r.id}`,
    product_name: r.category || r.title || "Logistics RFQ",
    party_name: r.buyer_name || "Buyer",
    amount: 0,
    due_label: "Today",
    status_label: String(r.status || "OPEN").toUpperCase(),
    status_key: r.status_key === "new" ? "open" : "pending",
    action_label: r.action || "View",
    href: r.href || "/dashboard/logistics/rfqs",
  }));

  return {
    ...payload,
    module: payload.module || "overview",
    pipeline_label: "Overview Pipeline",
    work_queue_label: "Overview Work Queue",
    status_alerts: statusAlerts,
    key_metrics: keyMetrics,
    overview_pipeline: overviewPipeline,
    work_queue: workQueue,
    performance_snapshot: [
      { key: "on_time", label: "On-time", value: `${perf.win_rate ?? 96}%` },
      { key: "response", label: "Response", value: perf.avg_response_time_hours ? `${perf.avg_response_time_hours} hrs` : "—" },
      { key: "open_risks", label: "Open risks", value: String(delayedTask?.count ?? 0) },
      { key: "pending_value", label: "Pending value", value: revenue ? `₹ ${Math.round(revenue / 1000)}K` : "₹ 0" },
    ],
    recent_activity: [
      { key: "rfqs", label: "Active RFQs", value: String(totalRfqs) },
      { key: "quotes", label: "Quotations", value: String(quoted) },
      { key: "deliveries", label: "Active deliveries", value: String(contracts) },
      { key: "contracts", label: "Contracts won", value: String(contracts) },
    ],
    exceptions: [
      { key: "delayed", label: "Delayed items", value: String(delayedTask?.count ?? 0) },
      { key: "approvals", label: "Pending approvals", value: String(pendingTask?.count ?? 0) },
      { key: "invoices", label: "Invoices pending", value: String(invoiceTask?.count ?? 0) },
      { key: "closing", label: "Closing soon", value: String(delayedTask?.count ?? 0) },
    ],
  };
}

function ensureUnifiedShape(payload) {
  const statusAlerts =
    Array.isArray(payload.status_alerts) && payload.status_alerts.length > 0
      ? payload.status_alerts
      : DEFAULT_ALERTS.map((a) => {
          const match = (payload.status_alerts || []).find((x) => x.key === a.key);
          return match || a;
        });

  const overviewPipeline =
    Array.isArray(payload.overview_pipeline) && payload.overview_pipeline.length > 0
      ? payload.overview_pipeline
      : PIPELINE_LABELS.map((step) => {
          const match = (payload.overview_pipeline || []).find((x) => x.key === step.key);
          return match || { ...step, count: 0 };
        });

  return {
    ...payload,
    status_alerts: statusAlerts,
    key_metrics: Array.isArray(payload.key_metrics) ? payload.key_metrics : [],
    overview_pipeline: overviewPipeline,
    work_queue: Array.isArray(payload.work_queue) ? payload.work_queue : [],
    performance_snapshot: Array.isArray(payload.performance_snapshot) ? payload.performance_snapshot : [],
    recent_activity: Array.isArray(payload.recent_activity) ? payload.recent_activity : [],
    exceptions: Array.isArray(payload.exceptions) ? payload.exceptions : [],
  };
}

export function normalizeLogisticsDashboardResponse(res) {
  const payload = unwrapApiPayload(res);
  if (!payload) return null;
  if (Array.isArray(payload.status_alerts) && payload.status_alerts.length > 0) {
    return ensureUnifiedShape(payload);
  }
  if (payload.kpis) {
    return mapLegacyMarketplaceDashboard(payload);
  }
  return ensureUnifiedShape(payload);
}
